/**
 * Regras puras de apresentação dos itens do Estoque.
 *
 * RELEASE: 2026-10-10 v1.8.0 - isStockStatusOperational: selo verde também para Instalado.
 * RELEASE: 2026-10-10 v1.7.0 - Situação Instalado (só sistema) e manualStatusesFor.
 * RELEASE: 2026-10-10 v1.6.0 - Kit em cliente/veículo só pode retornar manualmente ao estoque.
 * RELEASE: 2026-10-10 v1.5.0 - Centraliza o bloqueio de avanço sem itens ou destino.
 * RELEASE: 2026-10-10 v1.4.0 - Centraliza elegibilidade de destinos por situação e local atual.
 * RELEASE: 2026-10-09 v1.3.0 - Centraliza as cores das etiquetas de tipo de produto (Kit azul).
 * RELEASE: 2026-10-09 v1.2.0 - Centraliza a referência de OS exibida no histórico de movimentações.
 * RELEASE: 2026-10-08 v1.1.0 - Acrescenta rótulos de produto/destino e detalhes da movimentação.
 * RELEASE: 2026-10-08 v1.0.0 - Centraliza situações por tipo, data/hora e rótulos de local.
 */

export type StockPresentationKind = 'tracker' | 'chip' | 'kit';
export type StockPresentationPlaceType = 'location' | 'provider' | 'customer' | 'carrier'; // carrier: 2026-10-16 Logística

/** Dados mínimos para compor a identificação de uma movimentação. */
export interface StockMovementPresentation {
  item_type: StockPresentationKind;
  item_label: string;
  kit_code?: string | null;
  imei?: string | null;
  iccid?: string | null;
}

/** Situações permitidas para rastreadores. */
export const TRACKER_STATUSES = [
  { value: 'active', label: 'Ativo' },
  { value: 'pending_receipt', label: 'Recebimento pendente' },
  { value: 'damaged', label: 'Estragado' },
  { value: 'lost', label: 'Extraviado' },
  { value: 'maintenance', label: 'Manutenção' },
  { value: 'unavailable', label: 'Indisponível' },
  { value: 'homologation', label: 'Em homologação' },
  // BR-EST-028: definida só pelo sistema quando o rastreador chega ao veículo
  { value: 'installed', label: 'Instalado' },
  // 2026-10-11 BR-EST-031: definida só pela Homologação
  { value: 'indemnified', label: 'Indenizado' },
] as const;

/** Situações que só o sistema define (nunca escolhidas em formulários). */
export const SYSTEM_ONLY_STATUSES: readonly string[] = ['installed', 'indemnified'];

/** Situações permitidas para chips. */
export const CHIP_STATUSES = [
  { value: 'active', label: 'Ativo' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'pending_receipt', label: 'Recebimento pendente' },
  { value: 'paused', label: 'Pausado' },
] as const;

/** O kit montado é Ativo; Desmontado é derivado da operação de desmontagem. */
export const KIT_STATUSES = [{ value: 'active', label: 'Ativo' }] as const;

/** Retorna somente as situações válidas para o tipo informado. */
export function statusesFor(kind: StockPresentationKind) {
  if (kind === 'chip') return CHIP_STATUSES;
  if (kind === 'kit') return KIT_STATUSES;
  return TRACKER_STATUSES;
}

/** Situações que o usuário pode escolher em cadastro/importação (sem as do sistema). */
export function manualStatusesFor(kind: StockPresentationKind) {
  return (statusesFor(kind) as readonly { value: string; label: string }[])
    .filter((status) => !SYSTEM_ONLY_STATUSES.includes(status.value));
}

/** Rastreador Instalado equivale a Ativo para retirar do veículo (BR-EST-028). */
export function isTrackerOperational(status?: string | null): boolean {
  return status === 'active' || status === 'installed';
}

/**
 * Situação que conta como operacional por tipo de item (selo verde nas telas).
 * Chip e kit exigem "Ativo"; o rastreador também aceita "Instalado" (BR-EST-028).
 */
export function isStockStatusOperational(
  kind: StockPresentationKind,
  status?: string | null,
): boolean {
  return kind === 'tracker' ? isTrackerOperational(status) : status === 'active';
}

/** Converte a situação persistida no texto mostrado ao usuário. */
export function stockStatusLabel(kind: StockPresentationKind, value: string, dissolved = false): string {
  if (kind === 'kit' && dissolved) return 'Desmontado';
  return (statusesFor(kind) as readonly { value: string; label: string }[])
    .find((status) => status.value === value)?.label ?? value;
}

/** Formata instante com data, hora e minuto, respeitando o fuso do usuário. */
export function formatStockDateTime(
  value?: string | null,
  timeZone?: string,
): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

/** Rótulo do tipo do local atual. */
export function stockPlaceTypeLabel(type: StockPresentationPlaceType): string {
  return ({
    location: 'Endereço de estoque',
    provider: 'Prestador',
    customer: 'Cliente',
    carrier: 'Transportadora',
  } as const)[type];
}

/** Rótulo curto do produto movimentado. */
export function stockProductTypeLabel(type: StockPresentationKind): string {
  return ({ tracker: 'Rastreador', chip: 'Chip', kit: 'Kit' } as const)[type];
}

/**
 * Classes da etiqueta de tipo de produto (Kit, Rastreador, Chip).
 *
 * Cada tipo tem cor própria para leitura rápida na listagem; os tons são os
 * tokens semânticos do tema (info/success/warning), então continuam legíveis
 * no modo escuro. Kit usa azul.
 */
export function stockProductTypeBadgeClass(type: StockPresentationKind): string {
  return ({
    kit: 'bg-info/20 text-info',
    tracker: 'bg-success/20 text-success',
    chip: 'bg-warning/20 text-warning',
  } as const)[type];
}

/** Rótulo curto do destino usado nas etiquetas da movimentação. */
export function stockMovementDestinationLabel(type: StockPresentationPlaceType): string {
  return ({ location: 'Estoque', provider: 'Prestador', customer: 'Cliente', carrier: 'Transportadora' } as const)[type];
}

/** Linhas secundárias exibidas abaixo do identificador principal da movimentação. */
export function stockMovementDetails(movement: StockMovementPresentation): string[] {
  if (movement.item_type !== 'kit') return [];
  return [
    movement.imei ? `IMEI: ${movement.imei}` : null,
    movement.iccid ? `ICCID: ${movement.iccid}` : null,
  ].filter((value): value is string => Boolean(value));
}
/** Origem do pedido de movimentação (BR-OS-001). */
export type StockMovementSourceType = 'manual' | 'kit_formation' | 'kit_dissolution' | 'service_order' | 'shipment';

/** Exibe o número somente quando a movimentação foi originada por uma Ordem de Serviço. */
export function stockServiceOrderReference(
  type: StockMovementSourceType | null | undefined,
  reference?: string | null,
): string {
  return type === 'service_order' && reference?.trim() ? reference.trim() : '—';
}

/**
 * Rótulo da origem exibido na listagem/ficha; inclui a referência do documento
 * (ex.: número da OS ou código do kit) quando houver.
 */
export function stockMovementSourceLabel(type: StockMovementSourceType | null | undefined, reference?: string | null): string {
  const base = ({
    manual: 'Movimentação manual',
    kit_formation: 'Montagem de kit',
    kit_dissolution: 'Desmontagem de kit',
    service_order: 'Ordem de serviço',
    shipment: 'Remessa (Logística)',
  } as const)[type ?? 'manual'] ?? 'Movimentação manual';
  return reference ? `${base} · ${reference}` : base;
}

/** Dados mínimos de um item selecionável para validar a movimentação manual. */
export interface StockMovableRuleItem {
  type: StockPresentationKind;
  status?: string | null;
  tracker_status?: string | null;
  chip_status?: string | null;
  eligible?: boolean | null;
  place?: { type?: StockPresentationPlaceType | null } | null;
}

/**
 * Kit só pode ser movimentado com kit, rastreador e chip ativos (o servidor
 * informa `eligible`). Itens avulsos seguem as regras próprias do servidor.
 */
export function isStockMovableBlocked(item: StockMovableRuleItem): boolean {
  return false;
}

/** Confirma se o item e, no kit, ambos os componentes estão integralmente ativos. */
export function isStockMovableFullyActive(item: StockMovableRuleItem): boolean {
  if (item.type === 'kit') return item.eligible !== false
    && item.status === 'active'
    && isTrackerOperational(item.tracker_status)
    && item.chip_status === 'active';
  return item.status === 'active';
}

/** Identifica seleção com kit atualmente vinculado a cliente/veículo. */
export function hasCustomerLinkedKit(items: StockMovableRuleItem[]): boolean {
  return items.some((item) => item.type === 'kit' && item.place?.type === 'customer');
}

/** Prestador só recebe itens ativos que não sejam kits vinculados a cliente/veículo. */
export function canMoveSelectionToProvider(items: StockMovableRuleItem[]): boolean {
  return !hasCustomerLinkedKit(items) && items.every(isStockMovableFullyActive);
}

/** Cliente aceita somente kits completos: rastreador ou chip avulso bloqueia o destino. */
export function canMoveSelectionToCustomer(items: StockMovableRuleItem[]): boolean {
  return items.every((item) => item.type === 'kit'
    && isStockMovableFullyActive(item)
    && item.place?.type !== 'customer');
}

/** Garante os requisitos mínimos antes de avançar em cada etapa da movimentação. */
export function canAdvanceStockMovement(step: number, selectedCount: number, hasDestination: boolean): boolean {
  if (step === 1) return selectedCount > 0;
  if (step === 2) return selectedCount > 0 && hasDestination;
  return false;
}
